"use client";

import type { ReactNode } from "react";
import { AlertOctagon, Inbox, Lock, PlugZap, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Small shared building blocks used by every primitive to render the
 * "mandatory state matrix" (docs/COMPONENT_LIBRARY.md) consistently: loading,
 * empty, permission-restricted, provider-disconnected, failed, retrying and
 * the partial/stale banner. Kept deliberately generic so each primitive can
 * still keep its own card shape/skeleton silhouette around these parts.
 */

export function SkeletonLine({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-control bg-surface-secondary", className)} role="presentation" />;
}

export function StateMessage({
  icon,
  title,
  description,
  action,
  tone = "neutral",
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  tone?: "neutral" | "danger" | "warning";
}) {
  const toneClass = tone === "danger" ? "text-status-danger" : tone === "warning" ? "text-status-warning" : "text-text-secondary";
  return (
    <div className="flex flex-col items-start gap-2 py-2 text-start">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-control bg-surface-secondary", toneClass)}>{icon}</div>
      <p className="text-sm font-bold text-text-primary">{title}</p>
      {description ? <p className="text-xs text-text-secondary">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <StateMessage icon={<Inbox aria-hidden="true" size={18} />} title={title} description={description} action={action} />;
}

export function PermissionRestrictedState({ description }: { description?: string }) {
  return (
    <StateMessage
      icon={<Lock aria-hidden="true" size={18} />}
      title="Restricted for your role"
      description={description ?? "Ask a manager to grant access to this data for this location."}
    />
  );
}

export function ProviderDisconnectedState({
  providerName = "the connected provider",
  onReconnect,
}: {
  providerName?: string;
  onReconnect?: () => void;
}) {
  return (
    <StateMessage
      tone="warning"
      icon={<PlugZap aria-hidden="true" size={18} />}
      title="Integration disconnected"
      description={`We lost connection to ${providerName}. Data shown may be incomplete until it reconnects.`}
      action={
        onReconnect ? (
          <button
            type="button"
            onClick={onReconnect}
            className="rounded-control border border-border-strong bg-surface-primary px-3 py-1.5 text-xs font-bold text-text-primary hover:bg-surface-secondary"
          >
            Reconnect
          </button>
        ) : undefined
      }
    />
  );
}

export function FailedActionState({ description, onRetry }: { description?: string; onRetry?: () => void }) {
  return (
    <StateMessage
      tone="danger"
      icon={<AlertOctagon aria-hidden="true" size={18} />}
      title="Action failed"
      description={description ?? "This did not go through. Nothing has changed yet."}
      action={
        onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-control bg-status-danger px-3 py-1.5 text-xs font-bold text-text-inverse hover:opacity-90"
          >
            Try again
          </button>
        ) : undefined
      }
    />
  );
}

export function RetryingState({ description }: { description?: string }) {
  return (
    <div className="flex items-center gap-2 py-2 text-start text-text-secondary">
      <RefreshCw aria-hidden="true" size={16} className="animate-spin" />
      <div>
        <p className="text-sm font-bold text-text-primary">Retrying…</p>
        {description ? <p className="text-xs text-text-secondary">{description}</p> : null}
      </div>
    </div>
  );
}

export function PartialStaleBanner({ asOf }: { asOf?: string }) {
  return (
    <div className="mb-2 flex items-center gap-1.5 rounded-control bg-status-warning-bg px-2.5 py-1.5 text-xs font-semibold text-status-warning">
      <RefreshCw aria-hidden="true" size={12} />
      <span>{asOf ? `Data may be out of date · last synced ${asOf}` : "Data may be out of date"}</span>
    </div>
  );
}
